import { create } from 'zustand';
import { loadStoredConfigs } from '../utils/config/loadConfig';
import { validateConfig } from '../utils/config/configValidator';
import { handleAPIError } from '../utils/error/errorHandler';
import { generateTitle } from '../utils/titleGenerator';
import {
  getFirestore,
  doc,
  deleteDoc,
  updateDoc,
  getDoc,
  setDoc,
  writeBatch
} from 'firebase/firestore';
import {
  saveChatHistory,
  getChatHistory,
  saveApiConfig,
  getApiConfigs,
  saveChatMessage,
  getChatDocument,
  deleteApiKey
} from '../utils/firebase';
import type { AIModel, APIConfig, Message, ChatContext, ChatSession, ModelType } from '../types/models';
import type { User } from 'firebase/auth';
import type { UsagePreferences } from '../types/usage';
import { MODELS_DATABASE } from '../config/modelsDatabase';

const DEFAULT_CONTEXT: ChatContext = {
  userInfo: {},
  systemPrompt: ''
};

const DEFAULT_USAGE_PREFERENCES: UsagePreferences = {
  display_mode: 'monthly',
  currency: 'USD',
  alert_thresholds: {
    token_limit: [50, 80, 90],
    cost_limit: [50, 80, 90]
  },
  monthly_limits: {
    token_limit: 100000,
    cost_limit: 20
  }
};

interface State {
  models: AIModel[];
  selectedModel: AIModel | null;
  apiConfigs: APIConfig[];
  messages: Message[];
  chatHistory: ChatSession[];
  currentChatId: string | null;
  currentContext: ChatContext;
  isConfigPanelOpen: boolean;
  isLoading: boolean;
  error: string | null;
  initialized: boolean;
  user: User | null;
  language: string;
  showLandingPage: boolean;
  darkMode: boolean;
  topUpValue: number;
  usagePreferences: UsagePreferences;
  setUser: (user: User | null) => void;
  updateTopUpValue: (amount: number) => Promise<void>;
  loadUserChatHistory: () => Promise<void>;
  loadApiConfigs: () => Promise<void>;
  toggleConfigPanel: () => void;
  setError: (error: string | null) => void;
  addApiConfig: (config: APIConfig) => Promise<void>;
  removeApiConfig: (provider: string) => void;
  selectModel: (model: AIModel) => void;
  addMessage: (message: Message) => void;
  clearMessages: () => void;
  startNewChat: () => Promise<void>;
  loadChat: (chatId: string, messages?: Message[], context?: ChatContext) => Promise<void>;
  updateUserInfo: (key: string, value: string) => void;
  initializeModels: () => Promise<void>;
  deleteChat: (chatId: string) => Promise<void>;
  setLanguage: (language: string) => Promise<void>;
  loadLanguage: () => Promise<void>;
  setShowLandingPage: (show: boolean) => void;
  toggleDarkMode: () => void;
  initializeUserData: (userRef: any, topUpValue?: number) => Promise<void>;
  updateUserDataStructure: (userRef: any, userData: any) => Promise<void>;
}

export const useStore = create<State>((set, get) => ({
  models: [],
  selectedModel: null,
  apiConfigs: [],
  messages: [],
  chatHistory: [],
  currentChatId: null,
  currentContext: DEFAULT_CONTEXT,
  isConfigPanelOpen: false,
  isLoading: false,
  error: null,
  initialized: false,
  user: null,
  language: localStorage.getItem('language') || 'en',
  showLandingPage: true,
  darkMode: localStorage.getItem('darkMode') === 'true',
  topUpValue: 0,
  usagePreferences: DEFAULT_USAGE_PREFERENCES,

  setShowLandingPage: (show: boolean) => set({ showLandingPage: show }),

  updateTopUpValue: async (amount: number) => {
    const { user } = get();
    if (!user) return;
    
    try {
      if (amount < 0) {
        throw new Error('Top-up value cannot be negative');
      }

      const roundedAmount = Math.round(amount * 10000) / 10000;
      
      const db = getFirestore();
      const userRef = doc(db, 'users', user.uid);

      await updateDoc(userRef, {
        topUpValue: roundedAmount,
        lastUpdated: new Date().toISOString()
      });

      set({ topUpValue: roundedAmount });

      console.log('Updated top-up value:', {
        original: amount,
        rounded: roundedAmount,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error updating top-up value:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to update top-up value' });
    }
  },

  toggleDarkMode: () => set((state) => {
    const newDarkMode = !state.darkMode;
    localStorage.setItem('darkMode', String(newDarkMode));
    document.documentElement.classList.toggle('dark', newDarkMode);
    return { darkMode: newDarkMode };
  }),

  initializeUserData: async (userRef: any, topUpValue: number = 20) => {
    const currentMonth = new Date().toISOString().substring(0, 7);
    await setDoc(userRef, {
      topUpValue,
      usageSummary: {
        [currentMonth]: {
          total_tokens: 0,
          total_cost: 0,
          breakdown_by_model: {},
          limits: {
            monthly_token_limit: 100000,
            monthly_cost_limit: topUpValue,
            token_limit_remaining: 100000,
            cost_limit_remaining: topUpValue
          }
        }
      },
      usageHistory: [],
      usagePreferences: DEFAULT_USAGE_PREFERENCES,
      createdAt: new Date().toISOString(),
      lastUpdated: new Date().toISOString()
    });
    console.log('User data initialized with usage preferences');
  },

  updateUserDataStructure: async (userRef: any, userData: any) => {
    const currentMonth = new Date().toISOString().substring(0, 7);
    const topUpValue = userData?.topUpValue ?? 20;
    
    await updateDoc(userRef, {
      usageSummary: userData?.usageSummary || {
        [currentMonth]: {
          total_tokens: 0,
          total_cost: 0,
          breakdown_by_model: {},
          limits: {
            monthly_token_limit: 100000,
            monthly_cost_limit: topUpValue,
            token_limit_remaining: 100000,
            cost_limit_remaining: topUpValue
          }
        }
      },
      usageHistory: userData?.usageHistory || [],
      usagePreferences: userData?.usagePreferences || DEFAULT_USAGE_PREFERENCES,
      topUpValue,
      lastUpdated: new Date().toISOString()
    });
    console.log('User data structure updated with usage preferences');
  },

  setUser: async (user: User | null) => {
    console.log('Setting user:', user?.uid);
    set(state => ({
      ...state,
      user,
      isLoading: true,
      error: null
    }));

    if (user) {
      console.log('Loading user data...');
      
      set(state => ({
        ...state,
        chatHistory: [],
        messages: [],
        currentChatId: null,
        currentContext: DEFAULT_CONTEXT,
        apiConfigs: [],
        usagePreferences: DEFAULT_USAGE_PREFERENCES
      }));

      await get().initializeModels();

      try {
        const db = getFirestore();
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        const userData = userDoc.data();

        if (!userData) {
          console.log('Initializing new user data');
          await get().initializeUserData(userRef, 1);
        } else if (!userData.usageSummary || !userData.usageHistory || !userData.usagePreferences) {
          console.log('Updating user data structure');
          await get().updateUserDataStructure(userRef, { ...userData });
        }

        const currentUserDoc = await getDoc(userRef);
        const currentUserData = currentUserDoc.data();
        const topUpValue = currentUserData?.topUpValue ?? 0;
        
        await Promise.all([
          get().loadUserChatHistory(),
          get().loadApiConfigs(),
          get().loadLanguage()
        ]);

        set({
          topUpValue,
          usagePreferences: userData?.usagePreferences ?? DEFAULT_USAGE_PREFERENCES
        });
        
        console.log('User data loaded successfully');
        
        await get().initializeModels();

        set(state => ({
          ...state,
          isLoading: false,
          initialized: true
        }));
      } catch (error) {
        console.error('Error loading user data:', error);
        set(state => ({
          ...state,
          error: 'Failed to load user data',
          isLoading: false
        }));
      }
    } else {
      set(state => ({
        ...state,
        chatHistory: [],
        messages: [],
        currentChatId: null,
        currentContext: DEFAULT_CONTEXT,
        apiConfigs: [],
        models: [],
        error: null,
        initialized: false,
        isLoading: false,
        usagePreferences: DEFAULT_USAGE_PREFERENCES
      }));

      localStorage.clear();
    }
  },

  loadUserChatHistory: async () => {
    const { user } = get();
    if (!user) {
      console.log('No user found, skipping chat history load');
      return;
    }

    try {
      set({ isLoading: true });
      console.log('Loading chat history for user:', user.uid);
      const history = await getChatHistory(user.uid);
      console.log('Raw history from Firebase:', history);

      if (!history || history.length === 0) {
        console.log('No chat history found');
        set({ 
          chatHistory: [],
          isLoading: false,
          currentChatId: null,
          messages: []
        });
        return;
      }

      const chatHistory = history
        .map(data => ({
          id: data.id,
          title: data.title || 'New Chat',
          messages: data.messages || [],
          createdAt: data.createdAt || Date.now(),
          modelId: data.modelId,
          context: data.context || DEFAULT_CONTEXT
        }))
        .sort((a, b) => b.createdAt - a.createdAt) as ChatSession[];

      set({
        chatHistory,
        isLoading: false
      });

      const state = get();
      if (!state.currentChatId && chatHistory.length > 0) {
        const mostRecentChat = chatHistory[0];
        await get().loadChat(mostRecentChat.id);
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
      set({ 
        error: 'Failed to load chat history',
        isLoading: false 
      });
    }
  },

  loadApiConfigs: async () => {
    const { user } = get();
    if (!user) {
      console.log('No user found, skipping API configs load');
      return;
    }

    console.log('Loading API configs for user:', user.uid);
    set({ isLoading: true, error: null });

    try {
      Object.keys(localStorage)
        .filter(key => key.startsWith('api_config_'))
        .forEach(key => localStorage.removeItem(key));

      const configs = await getApiConfigs(user.uid);
      console.log('Loading fresh API configs from Firebase:', configs.map(c => ({ provider: c.provider })));

      set({
        apiConfigs: configs,
        isLoading: false
      });

      console.log('API configs loaded successfully');
    } catch (error) {
      console.error('Error loading API configs:', error);
      set({ 
        error: 'Failed to load API configurations. Please try again.',
        isLoading: false 
      });
    }
  },

  toggleConfigPanel: () => {
    set((state) => ({ isConfigPanelOpen: !state.isConfigPanelOpen }));
  },

  setError: (error: string | null) => set({ error }),

  addApiConfig: async (config: APIConfig) => {
    const { user } = get();
    if (!user) {
      throw new Error('User not authenticated');
    }

    set({ isLoading: true, error: null });
    
    try {
      const error = validateConfig(config);
      if (error) {
        throw new Error(error);
      }

      await saveApiConfig(user.uid, config);
      console.log('API config saved to Firebase');

      set((state) => {
        const updatedConfigs = [
          ...state.apiConfigs.filter(c => c.provider !== config.provider),
          config
        ];

        return {
          apiConfigs: updatedConfigs,
          isLoading: false,
          initialized: true
        };
      });

      // Reinitialize models after adding new API config
      await get().initializeModels();
    } catch (error) {
      console.error('Error adding API config:', error);
      set({ 
        error: handleAPIError(error, config.provider),
        isLoading: false 
      });
    }
  },

  removeApiConfig: async (provider: string) => {
    const { user } = get();
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      await deleteApiKey(user.uid, provider);

      set((state) => ({
        apiConfigs: state.apiConfigs.filter(c => c.provider !== provider),
        models: state.models.filter(m => m.provider !== provider),
        selectedModel: state.selectedModel?.provider === provider ? null : state.selectedModel
      }));

      localStorage.removeItem(`api_config_${provider}`);
      localStorage.removeItem(`models_${provider}`);

      console.log('API config removed successfully:', provider);
    } catch (error) {
      console.error('Error removing API config:', error);
      throw error;
    }
  },

  selectModel: (model: AIModel) => {
    console.log('Selecting model:', model.name);
    const { currentChatId, apiConfigs, topUpValue } = get();

    const hasUserApiKey = apiConfigs.some(config => config.provider === model.provider);

    // Get provider name for system API key
    const providerMap: Record<string, string> = {
      'anthropic': 'ANTHROPIC',
      'openai': 'OPENAI',
      'groq': 'GROQ',
      'gemini': 'GEMINI',
      'deepseek': 'DEEPSEEK',
      'minimax': 'MINIMAX',
      'cerebras': 'CEREBRAS',
      'qwen': 'QWEN',
      'mistral': 'MISTRAL',
      'xai': 'XAI'
    };

    const normalizedProvider = providerMap[model.provider] || model.provider.toUpperCase();
    const hasSystemApi = !!import.meta.env[`SYSTEM_${normalizedProvider}_API_KEY`];

    // Check if model is usable
    if (!hasUserApiKey && (!hasSystemApi || topUpValue <= 0)) {
      set({
        error: hasSystemApi
          ? 'You need to add funds to use this model'
          : `You need to configure an API key for ${model.provider}`
      });
      return;
    }

    // Clear any previous errors
    set({ error: null });

    set((state) => {
      if (currentChatId) {
        console.log('Switching model within current chat:', {
          messagesCount: state.messages.length,
          context: state.currentContext,
          chatId: currentChatId
        });

        return {
          selectedModel: model,
          messages: state.messages,
          currentContext: state.currentContext,
          currentChatId: currentChatId,
          error: null
        };
      }

      console.log('Setting model without active chat');
      return {
        selectedModel: model,
        messages: [],
        currentContext: DEFAULT_CONTEXT,
        currentChatId: null,
        error: null
      };
    });
  },

  addMessage: async (message: Message) => {
    const { currentChatId, chatHistory, currentContext, user } = get();
    if (!user || !currentChatId) return;
    
    try {
      const currentChat = chatHistory.find(chat => chat.id === currentChatId);
      if (!currentChat) {
        console.error('Current chat not found:', currentChatId);
        return;
      }

      const updatedMessages = [...currentChat.messages, message];
      
      let title = currentChat.title;
      const isFirstMessage = currentChat.messages.length === 0;
      if (message.role === 'user' && (!title || title === 'New Chat')) {
        const newTitle = message.content?.slice(0, 50) + '...';
        title = newTitle;
      
        set((state) => ({
          chatHistory: state.chatHistory.map((chat) =>
            chat.id === currentChatId ? { ...chat, title } : chat
          ),
        }));
      }

      const updatedChat = {
        ...currentChat,
        messages: updatedMessages,
        context: currentContext,
        title
      };

      set((state) => ({
        messages: updatedMessages,
        chatHistory: state.chatHistory.map(chat =>
          chat.id === currentChatId ? updatedChat : chat
        )
      }));

      const firestoreMessage = {
        ...message,
        userId: user.uid,
        userName: user.displayName || 'User',
        timestamp: Date.now()
      };
      
      await saveChatMessage(firestoreMessage, currentChatId);
      console.log('Message saved to Firebase for chat:', currentChatId);
    } catch (error) {
      console.error('Error adding message:', error);
      set({ error: 'Failed to save message' });
    }
  },

  clearMessages: async () => {
    const { selectedModel } = get();
    if (!selectedModel) {
      set({ error: 'Please select a model first' });
      return;
    }
    await get().startNewChat();
  },

  startNewChat: async () => {
    const { selectedModel, user, currentChatId } = get();
    if (!user) {
      console.error('Cannot start new chat: User not authenticated');
      return;
    }

    try {
      set({
        messages: [],
        currentChatId: null,
        currentContext: DEFAULT_CONTEXT,
        selectedModel: selectedModel,
        error: null
      });

      const timestamp = Date.now();
      const newChat: ChatSession = {
        id: timestamp.toString(),
        title: '',
        messages: [],
        createdAt: timestamp,
        modelId: selectedModel ? selectedModel.id : '',
        context: { ...DEFAULT_CONTEXT }
      };

      console.log('Creating new chat:', {
        chatId: newChat.id,
        title: newChat.title,
        modelId: newChat.modelId,
        previousChatId: currentChatId
      });

      await saveChatHistory(user.uid, newChat);
      console.log('New chat saved to Firebase successfully');

      set((state) => ({
        currentChatId: newChat.id,
        messages: [],
        chatHistory: [newChat, ...state.chatHistory],
        currentContext: DEFAULT_CONTEXT
      }));

      const currentState = get();
      if (currentState.currentChatId !== newChat.id) {
        console.error('Chat creation validation failed');
        set({
          messages: [],
          currentChatId: null,
          currentContext: DEFAULT_CONTEXT,
          error: 'Failed to create new chat'
        });
        return;
      }

      console.log('New chat successfully created and validated');
    } catch (error) {
      console.error('Error creating new chat:', error);
      set({ error: 'Failed to create new chat' });
    }
  },

  loadChat: async (chatId: string, messages?: Message[], context?: ChatContext) => {
    console.log('Loading chat:', chatId);
    const { user, chatHistory, models, apiConfigs } = get();
    if (!user) return;

    try {
      set({ isLoading: true, error: null });

      const existingChat = chatHistory.find(chat => chat.id === chatId);
      if (existingChat && !messages) {
        console.log('Loading chat from local state:', chatId);
        set({
          currentChatId: chatId,
          messages: existingChat.messages,
          selectedModel: models.find(m => m.id === existingChat.modelId) || null,
          currentContext: existingChat.context || DEFAULT_CONTEXT,
          isLoading: false
        });
        return;
      }

      const chatDoc = await getChatDocument(user.uid, chatId);
      if (!chatDoc.exists()) {
        console.error('Chat document not found:', chatId);
        set({
          error: 'Chat not found',
          isLoading: false
        });
        return;
      }

      const chatData = chatDoc.data();
      if (!chatData) {
        console.error('Chat data is empty:', chatId);
        set({
          error: 'Chat data is empty',
          isLoading: false
        });
        return;
      }

      console.log('Loaded chat from Firebase:', {
        id: chatId,
        title: chatData.title,
        messages: chatData.messages?.length || 0,
        modelId: chatData.modelId
      });

      let selectedModel: AIModel | null = null;
      const originalModel = models.find(m => m.id === chatData.modelId);
      
      if (originalModel) {
        console.log('Found original model:', originalModel.name);
        const config = apiConfigs.find(c => c.provider === originalModel.provider);
        if (!config) {
          console.error('No API config found for provider:', originalModel.provider);
          set({
            currentChatId: chatId,
            messages: [],
            selectedModel: null,
            currentContext: DEFAULT_CONTEXT,
            error: `Please configure API key for ${originalModel.provider} to view this chat`,
            isLoading: false
          });
          return;
        }
        selectedModel = originalModel;
      } else {
        console.warn('Original model not found for chat:', chatData.modelId);
        const chatProvider = chatData.modelId?.split('/')[0];
        if (chatProvider) {
          const compatibleModel = models.find(m => m.provider === chatProvider);
          if (compatibleModel) {
            console.log('Found compatible model:', compatibleModel.name);
            selectedModel = compatibleModel;
          }
        }
      }

      const title = chatData.title || (chatData.messages?.length > 0
        ? generateTitle(chatData.messages)
        : 'New Chat');

      set({
        currentChatId: chatId,
        messages: messages || chatData.messages || [],
        selectedModel,
        currentContext: context || chatData.context || DEFAULT_CONTEXT,
        isLoading: false,
        error: selectedModel ? null : 'The model used in this chat is no longer available'
      });

      set(state => ({
        chatHistory: [
          {
            id: chatId,
            title,
            messages: messages || chatData.messages || [],
            createdAt: chatData.createdAt || Date.now(),
            modelId: chatData.modelId,
            context: context || chatData.context || DEFAULT_CONTEXT
          },
          ...state.chatHistory.filter(chat => chat.id !== chatId)
        ]
      }));

      console.log('Chat loaded successfully:', chatId);
    } catch (error) {
      console.error('Error loading chat:', error);
      set({
        error: 'Failed to load chat',
        messages: [],
        selectedModel: null,
        currentContext: DEFAULT_CONTEXT
      });
    }
  },

  updateUserInfo: (key: string, value: string) => {
    set((state) => {
      const newContext = {
        ...state.currentContext,
        userInfo: {
          ...state.currentContext.userInfo,
          [key]: value
        }
      };

      const updatedHistory = state.chatHistory.map(chat => 
        chat.id === state.currentChatId
          ? { ...chat, context: newContext }
          : chat
      );

      return {
        currentContext: newContext,
        chatHistory: updatedHistory
      };
    });
  },

  deleteChat: async (chatId: string) => {
    const { user, currentChatId, chatHistory } = get();
    if (!user) return;

    try {
      const db = getFirestore();
      const chatRef = doc(db, 'users', user.uid, 'chatHistory', chatId);

      const { DocumentService } = await import('../modules/fileHandler/services/supabase.service');
      const documentService = DocumentService.getInstance();
      
      try {
        const result = await documentService.deleteDocumentsByFilename('*', chatId, user.uid);
        console.log(`Deleted ${result} documents from Supabase for chat:`, chatId);
      } catch (error) {
        console.error('Error deleting documents from Supabase:', error);
        throw error;
      }

      await deleteDoc(chatRef);
      console.log('Chat deleted from Firebase:', chatId);

      // Update the local state without triggering a full reload
      set(state => ({
        chatHistory: state.chatHistory.filter(chat => chat.id !== chatId),
        messages: state.currentChatId === chatId ? [] : state.messages,
        currentChatId: state.currentChatId === chatId ? null : state.currentChatId,
        currentContext: state.currentChatId === chatId ? DEFAULT_CONTEXT : state.currentContext,
        selectedModel: state.currentChatId === chatId ? null : state.selectedModel,
        error: null
      }));

      const currentState = get();
      if (currentState.currentChatId === chatId) {
        console.error('Chat deletion state validation failed');
        set({
          messages: [],
          currentChatId: null,
          currentContext: DEFAULT_CONTEXT,
          selectedModel: null,
          error: 'Failed to properly delete chat'
        });
      }

      console.log('Chat successfully deleted and state validated');
      
      // If chat history is now empty or current chat was deleted, load chats for proper initialization
      if (chatHistory.length <= 1 || currentChatId === chatId) {
        await get().loadUserChatHistory();
      }
    } catch (error) {
      console.error('Error deleting chat:', error);
      set({
        error: 'Failed to delete chat',
        messages: currentChatId === chatId ? [] : get().messages,
        currentChatId: currentChatId === chatId ? null : currentChatId
      });
    }
  },

  setLanguage: async (language: string) => {
    const { user } = get();
    if (!user) return;
    
    try {
      const db = getFirestore();
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, { language });
      set({ language });
    } catch (error) {
      console.error('Error saving language preference:', error);
      set({ error: 'Failed to save language preference' });
    }
  },

  loadLanguage: async () => {
    const { user } = get();
    if (!user) return;
    
    try {
      const db = getFirestore();
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData?.language) {
          set({ language: userData.language });
        }
      }
    } catch (error) {
      console.error('Error loading language preference:', error);
      set({ error: 'Failed to load language preference' });
    }
  },

  initializeModels: async () => {
    console.log('Initializing models...');
    set({ isLoading: true, error: null, initialized: false });

    try {
      const { apiConfigs } = get();
      
      // Convert database models to AIModel array
      const models = Object.values(MODELS_DATABASE).map(model => ({
        ...model,
        isConfigured: apiConfigs.some(config => config.provider === model.provider),
        systemApiCost: model.pricing.prompt_tokens_per_1k * 1000 // Convert to per 1M tokens
      }));

      // Sort models by name
      const sortedModels = models.sort((a, b) => a.name.localeCompare(b.name));

      console.log('Models initialized:', sortedModels.length);
      set({
        models: sortedModels,
        isLoading: false,
        initialized: true
      });
    } catch (error) {
      console.error('Error initializing models:', error);
      set({ 
        error: 'Failed to initialize models',
        isLoading: false 
      });
    }
  }
}));